'use client'

/**
 * THE drop target drawn while files are dragged (#14562, #16101, #16117):
 * one look everywhere a page or panel takes a dropped file — the note page,
 * Home, the Handbook, and the AI Chat panel. It takes no pointer events, so
 * the drop lands on the element beneath it (`usePageFileIntake` /
 * `useDropTargetFileIntake`), and it is `aria-hidden`: a visual affordance
 * for a pointer gesture, with each surface's own keyboard path and live
 * region beside it.
 *
 * Two placements, one style:
 *  - `page` — fixed over the viewport, stopping at the docked AI Chat
 *    panel's edge (`useSidecarWidth`). The panel is a drop target of its
 *    own that attaches to the chat composer, so a page's overlay must not
 *    say a drop there goes to the page.
 *  - `panel` — over the element it sits in (the chat panel).
 */

import type { ReactNode } from 'react'
import { useSidecarWidth } from '@/components/app-frame/app-frame-context'
import { cn } from '@/lib/utils'

export function DropOverlay({
  placement,
  icon,
  title,
  detail,
  testId,
}: {
  placement: 'page' | 'panel'
  icon: ReactNode
  title: string
  detail?: string | null
  testId: string
}) {
  const sidecarWidth = useSidecarWidth()
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className={cn(
        'pointer-events-none bg-scrim-light p-4',
        placement === 'page'
          ? 'fixed inset-y-0 left-0 z-50'
          : 'absolute inset-0 z-10',
      )}
      style={placement === 'page' ? { right: sidecarWidth } : undefined}
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-stroke-selected bg-surface-elevated/80 p-6 text-center">
        <span className="text-text-secondary [&_svg]:h-8 [&_svg]:w-8">
          {icon}
        </span>
        <p className="text-base font-semibold text-text-primary">{title}</p>
        {detail ? (
          <p className="text-sm text-text-secondary">{detail}</p>
        ) : null}
      </div>
    </div>
  )
}
